// lib/constants.ts - Medical App Constants

// ==================================================
// UI CONSTANTS (includes search config)
// ==================================================
export const UI_CONSTANTS = {
  /** Minimum characters before drug typeahead search fires */
  MIN_DRUG_SEARCH_LENGTH: 3,
  /** Minimum characters before patient typeahead search fires */
  MIN_PATIENT_SEARCH_LENGTH: 2,
  MAX_AUTOCOMPLETE_SUGGESTIONS: 8,
  DEBOUNCE_DELAY: 300,
  ANIMATION_DURATION: 200,
  MAX_RECENT_ITEMS: 10,
};

// ==================================================
// DOSAGE OPTIONS — form-aware
// ==================================================

export const TABLET_DOSAGE_OPTIONS = [
  // Full tablet — ordered by prescription frequency
  "1-0-1",      // Twice daily — most commonly prescribed
  "1-1-1",      // Three times daily — antibiotics, most drugs
  "0-0-1",      // Once daily night — antacids, sleep meds, statins
  "1-0-0",      // Once daily morning — BP, thyroid, vitamins
  "1-1-1-1",   // Four times daily — some antibiotics
  "0-1-0",      // Once daily afternoon
  "1-1-0",      // Twice daily morning + afternoon
  "0-1-1",      // Twice daily afternoon + night
  "0-0-2",      // Two tablets at night
  "2-0-0",      // Two tablets morning
  "2-2-2",      // High dose TID
  // Half tablet patterns — filtered out for non-splittable tablets
  "½-0-½",
  "½-0-0",
  "0-0-½",
  "1-0-½",
  "½-½-½",
  // Special
  "SOS",
  "As directed",
];

export const SYRUP_DOSAGE_OPTIONS = [
  "5ml-0-5ml",
  "10ml-0-10ml",
  "5ml-5ml-5ml",
  "10ml-10ml-10ml",
  "2.5ml-0-2.5ml",    // Paediatric standard
  "2ml-0-2ml",        // Paediatric small
  "1.5ml-0-1.5ml",    // Paediatric infant
  "1ml-0-1ml",        // Neonatal
  "5ml-0-0",
  "10ml-0-0",
  "SOS",
  "As directed",
];

export const INHALER_DOSAGE_OPTIONS = [
  "1 puff-0-1 puff",
  "2 puffs-0-2 puffs",
  "1 puff-1 puff-1 puff",
  "2 puffs-2 puffs-2 puffs",
  "1 puff SOS",
  "2 puffs SOS",
  "As directed",
];

export const INJECTION_DOSAGE_OPTIONS = [
  "1 amp",
  "1 vial",
  "½ amp",
  "As directed",
];

export const TOPICAL_DOSAGE_OPTIONS = [
  "Apply twice daily",
  "Apply once daily",
  "Apply three times daily",
  "Apply at night",
  "Apply and cover",
  "Apply SOS",
  "As directed",
];

export const DROPS_DOSAGE_OPTIONS = [
  "1 drop-0-1 drop",
  "2 drops-0-2 drops",
  "1 drop-1 drop-1 drop",
  "2 drops-2 drops-2 drops",
  "SOS",
  "As directed",
];

export const VAGINAL_DOSAGE_OPTIONS = [
  "1 pessary at bedtime",
  "1 applicator at bedtime",
  "Per vaginum once daily",
  "Per vaginum twice daily",
  "As directed",
];

export const PAEDIATRIC_DOSAGE_OPTIONS = [
  "As per weight",
  "As per age",
  "½-0-½",
  "½-½-½",
  "¼-0-¼",
  "¼-¼-¼",
  "2.5ml-0-2.5ml",
  "5ml-0-5ml",
  "5ml-5ml-5ml",
  "2.5ml-2.5ml-2.5ml",
  "1ml-0-1ml",
  "SOS",
  "As directed",
];

/** Maps drug_catalog.form → dosage options list */
export const DOSAGE_OPTIONS_BY_FORM: Record<string, string[]> = {
  tablet:      TABLET_DOSAGE_OPTIONS,
  capsule:     TABLET_DOSAGE_OPTIONS,
  syrup:       SYRUP_DOSAGE_OPTIONS,
  suspension:  SYRUP_DOSAGE_OPTIONS,
  inhaler:     INHALER_DOSAGE_OPTIONS,
  injection:   INJECTION_DOSAGE_OPTIONS,
  cream:       TOPICAL_DOSAGE_OPTIONS,
  ointment:    TOPICAL_DOSAGE_OPTIONS,
  gel:         TOPICAL_DOSAGE_OPTIONS,
  drops:       DROPS_DOSAGE_OPTIONS,
  pessary:     VAGINAL_DOSAGE_OPTIONS,
};

/** Suffixes that indicate a tablet cannot be split safely */
const UNSPLITTABLE_SUFFIXES = [
  "XL", "XR", "SR", "CR", "LA", "ER",  // extended release
  "EC", "DR",                            // enteric coated
];

export interface Drug {
  brand_name: string;
  form: string;
}

/** True when the brand name marks an extended-release / enteric-coated product */
export function hasUnsplittableSuffix(brandName: string): boolean {
  const name = brandName.toUpperCase();
  return UNSPLITTABLE_SUFFIXES.some(
      (suffix) => name.includes(`-${suffix}`) || name.endsWith(` ${suffix}`)
  );
}

export function isTabletSplittable(drug: Drug): boolean {
  if (drug.form.toLowerCase() !== "tablet") return false;
  return !hasUnsplittableSuffix(drug.brand_name);
}

/**
 * Returns the correct dosage options for a given drug.
 * Filters out half-tablet options for non-splittable tablets (SR, XL, EC etc).
 * Pass isPaediatric=true to get weight/age-based options prepended.
 */
export function getDosageOptions(drug: Drug, isPaediatric = false): string[] {
  const options =
      DOSAGE_OPTIONS_BY_FORM[drug.form.toLowerCase()] ?? TABLET_DOSAGE_OPTIONS;

  let result = options;

  // Remove half-tablet patterns for non-splittable tablets
  if (drug.form.toLowerCase() === "tablet" && !isTabletSplittable(drug)) {
    result = result.filter((opt) => !opt.includes("½"));
  }

  // Prepend paediatric weight/age options for paediatric patients
  if (isPaediatric && (drug.form === "tablet" || drug.form === "capsule")) {
    const paedPrefix = ["As per weight", "As per age"];
    result = [...paedPrefix, ...result.filter((o) => !paedPrefix.includes(o))];
  }

  return result;
}

// ==================================================
// WHEN TO TAKE OPTIONS
// ==================================================
export const WHEN_OPTIONS = [
  // Meal-based — most common
  "After Food",
  "Before Food",
  "With Food",
  "Empty Stomach",

  // Time-based
  "Bedtime",
  "Morning",
  "Evening",
  "Afternoon",

  // Interval-based
  "Every 8 hours",
  "Every 12 hours",

  // Frequency modifiers
  "On alternate days",
  "Weekly",
  "Twice weekly",

  // Gynaecology cycle-based
  "Day 1-5 of cycle",
  "Day 2-6 of cycle",
  "Day 21 of cycle",
  "Continuously",

  // Orthopaedic activity-based
  "Before physiotherapy",
  "After physiotherapy",
  "Before activity",
  "After activity",

  // Special
  "SOS",
  "As needed",
  "30 min before food",
  "1 hour after food",
];

// ==================================================
// DURATION OPTIONS (for prescriptions)
// ==================================================
export const DURATION_OPTIONS = [
  // Short term — most prescribed first
  "3 days",
  "5 days",
  "7 days",
  "10 days",
  "14 days",
  "1 month",
  "2 months",
  "3 months",
  "6 months",

  // Open ended
  "Till review",
  "Continue",
  "Long term",
  "As directed",
];

// ==================================================
// ROUTE OF ADMINISTRATION OPTIONS
// ==================================================
export const ROUTE_OPTIONS = [
  "Oral",
  "Topical",
  "Injection",
  "Inhalation",
  "Ophthalmic",
  "Otic",
  "Other",
];

// ==================================================
// DURATION OPTIONS (for complaints)
// ==================================================
export const COMPLAINT_DURATION_OPTIONS = [
  // Acute
  "Since today",
  "Since yesterday",
  "2 days",
  "3 days",
  "4 days",
  "5 days",
  "1 week",
  "2 weeks",
  "3 weeks",

  // Subacute
  "1 month",
  "2 months",
  "3 months",
  "6 months",

  // Chronic
  "1 year",
  "2 years",
  "Long standing",
  "Since birth",
  "Since childhood",
  "Since vaccination",
  "Since weaning",
];

// ==================================================
// COMPLAINT SEVERITY OPTIONS
// ==================================================
export const SEVERITY_OPTIONS = [
  "Mild",
  "Moderate",
  "Severe",
  "Very severe",
];

// ==================================================
// COMPLAINT FREQUENCY OPTIONS
// ==================================================
export const FREQUENCY_OPTIONS = [
  "Continuous",
  "Intermittent",
  "Multiple times daily",
  "3-4 times daily",
  "Twice daily",
  "Once daily",
  "Occasionally",
  "At night only",
  "In morning",
  "After meals",
  "Before meals",
  "On exertion",
  "At rest",
];

// ==================================================
// MEDICAL SPECIALTIES
// ==================================================
export const MEDICAL_SPECIALTIES = [
  "General Practitioner",
  "General Medicine",
  "Cardiology",
  "Endocrinology",
  "Gastroenterology",
  "Pulmonology",
  "Nephrology",
  "Neurology",
  "Psychiatry",
  "Dermatology",
  "Orthopedics",
  "Ophthalmology",
  "ENT",
  "Gynecology",
  "Pediatrics",
  "Surgery",
];

// ==================================================
// VITAL SIGNS NORMAL RANGES — Adults
// ==================================================
export const VITAL_RANGES = {
  BP_SYSTOLIC:      { min: 90,   max: 140,  normal: "90-140"    },
  BP_DIASTOLIC:     { min: 60,   max: 80,   normal: "60-80"     },
  PULSE:            { min: 60,   max: 100,  normal: "60-100"    },
  TEMPERATURE:      { min: 96.8, max: 99.5, normal: "96.8-99.5" },
  SPO2:             { min: 95,   max: 100,  normal: "95-100"    },
  RESPIRATORY_RATE: { min: 12,   max: 20,   normal: "12-20"     },
  WEIGHT:           { min: 30,   max: 200,  normal: "Variable"  },
  HEIGHT:           { min: 100,  max: 220,  normal: "Variable"  },
  BMI:              { min: 18.5, max: 24.9, normal: "18.5-24.9" },
  // Gynaecology
  FUNDAL_HEIGHT:       { min: 0, max: 40,  normal: "Weeks = cm after 20wks" },
  FOETAL_HEART_RATE:   { min: 110, max: 160, normal: "110-160" },
};

// ==================================================
// VITAL SIGNS NORMAL RANGES — Paediatric (age-banded)
// ==================================================
export const PAEDIATRIC_VITAL_RANGES = {
  NEONATE: {           // 0–28 days
    PULSE:            { min: 100, max: 160, normal: "100-160" },
    RESPIRATORY_RATE: { min: 40,  max: 60,  normal: "40-60"   },
    BP_SYSTOLIC:      { min: 60,  max: 90,  normal: "60-90"   },
    SPO2:             { min: 95,  max: 100, normal: "95-100"  },
    TEMPERATURE:      { min: 96.8, max: 99.5, normal: "96.8-99.5" },
  },
  INFANT: {            // 1–12 months
    PULSE:            { min: 100, max: 160, normal: "100-160" },
    RESPIRATORY_RATE: { min: 30,  max: 60,  normal: "30-60"   },
    BP_SYSTOLIC:      { min: 70,  max: 100, normal: "70-100"  },
    SPO2:             { min: 95,  max: 100, normal: "95-100"  },
    TEMPERATURE:      { min: 96.8, max: 99.5, normal: "96.8-99.5" },
  },
  TODDLER: {           // 1–3 years
    PULSE:            { min: 90,  max: 150, normal: "90-150"  },
    RESPIRATORY_RATE: { min: 24,  max: 40,  normal: "24-40"   },
    BP_SYSTOLIC:      { min: 80,  max: 110, normal: "80-110"  },
    SPO2:             { min: 95,  max: 100, normal: "95-100"  },
    TEMPERATURE:      { min: 96.8, max: 99.5, normal: "96.8-99.5" },
  },
  CHILD: {             // 3–12 years
    PULSE:            { min: 70,  max: 120, normal: "70-120"  },
    RESPIRATORY_RATE: { min: 18,  max: 30,  normal: "18-30"   },
    BP_SYSTOLIC:      { min: 90,  max: 120, normal: "90-120"  },
    SPO2:             { min: 95,  max: 100, normal: "95-100"  },
    TEMPERATURE:      { min: 96.8, max: 99.5, normal: "96.8-99.5" },
  },
  ADOLESCENT: {        // 12–18 years
    PULSE:            { min: 60,  max: 100, normal: "60-100"  },
    RESPIRATORY_RATE: { min: 12,  max: 20,  normal: "12-20"   },
    BP_SYSTOLIC:      { min: 100, max: 130, normal: "100-130" },
    SPO2:             { min: 95,  max: 100, normal: "95-100"  },
    TEMPERATURE:      { min: 96.8, max: 99.5, normal: "96.8-99.5" },
  },
};

export type PaediatricAgeGroup = keyof typeof PAEDIATRIC_VITAL_RANGES;

/**
 * Returns the correct vital range group based on patient age in months.
 * Pass ageInMonths derived from patient.date_of_birth.
 */
export function getPaediatricAgeGroup(ageInMonths: number): PaediatricAgeGroup {
  if (ageInMonths < 1)   return "NEONATE";
  if (ageInMonths < 12)  return "INFANT";
  if (ageInMonths < 36)  return "TODDLER";
  if (ageInMonths < 144) return "CHILD";
  return "ADOLESCENT";
}

// ==================================================
// FORM VALIDATION RULES
// ==================================================
export const VALIDATION_RULES = {
  REQUIRED_FIELDS: ["patient_id", "date"],
  MIN_CONTENT_REQUIRED: 1,
  MAX_COMPLAINTS: 10,
  MAX_MEDICINES: 15,
  MAX_INVESTIGATIONS: 20,
};

// ==================================================
// DATE/TIME FORMATS
// ==================================================
export const DATE_FORMATS = {
  DISPLAY:      "MMM dd, yyyy",
  INPUT:        "yyyy-MM-dd",
  API:          "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
  TIME_DISPLAY: "h:mm a",
};

// ==================================================
// APPOINTMENT STATUS
// ==================================================
export const APPOINTMENT_STATUS = {
  SCHEDULED:   "scheduled",
  CONFIRMED:   "confirmed",     // patient confirmed via call/SMS
  COMPLETED:   "completed",
  CANCELLED:   "cancelled",
  NO_SHOW:     "no_show",
  RESCHEDULED: "rescheduled",
};

// ==================================================
// PRESCRIPTION STATUS
// ==================================================
export const PRESCRIPTION_STATUS = {
  ACTIVE:       "active",
  COMPLETED:    "completed",
  DISCONTINUED: "discontinued",
};